import { useCallback, useEffect, useState } from 'react';
import { Users, CirclePower, Search, VenetianMask, Bot } from 'lucide-react';
import { apiUsers } from '../api/client.js';

/**
 * Gestion des comptes utilisateurs (ADMIN only).
 *
 * Permet de valider (activer) les comptes en attente (PENDING), de désactiver
 * un compte, et de changer son rôle. Source de vérité côté backend : les
 * changements prennent effet immédiatement (le middleware relit role/status à
 * chaque requête).
 */

const ROLES = ['ADMIN', 'MANAGER', 'USER', 'VIEWER'];

const STATUS_FILTERS = [
  { key: 'all', label: 'Tous' },
  { key: 'PENDING', label: 'En attente' },
  { key: 'ACTIVE', label: 'Actifs' },
  { key: 'DISABLED', label: 'Désactivés' },
];

const STATUS_META = {
  PENDING: { label: 'En attente', color: 'var(--warning)' },
  ACTIVE: { label: 'Actif', color: 'var(--success)' },
  DISABLED: { label: 'Désactivé', color: 'var(--danger)' },
};

const pill = (active) => ({
  borderRadius: 999,
  padding: '6px 14px',
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
  border: '1px solid var(--glass-border)',
  background: active ? 'var(--accent)' : 'transparent',
  color: active ? '#fff' : 'var(--text)',
});

const iconBtn = {
  background: 'transparent',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-sm)',
  padding: 6,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
};

const selectStyle = {
  padding: '6px 8px',
  background: 'var(--glass)',
  color: 'var(--text)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 13,
};

function StatusBadge({ status }) {
  const meta = STATUS_META[status] ?? { label: status, color: 'var(--text-muted)' };
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 12,
        fontWeight: 500,
        color: meta.color,
      }}
    >
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: 999,
          background: meta.color,
          display: 'inline-block',
        }}
      />
      {meta.label}
    </span>
  );
}

export default function UsersView({ user, onImpersonate }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [q, setQ] = useState('');
  const [busyId, setBusyId] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { items: data } = await apiUsers.list({
        status: statusFilter === 'all' ? undefined : statusFilter,
        q: q.trim() || undefined,
      });
      setItems(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, q]);

  useEffect(() => {
    reload();
  }, [reload]);

  const patch = async (id, body) => {
    setBusyId(id);
    setError(null);
    try {
      const updated = await apiUsers.update(id, body);
      setItems((list) => list.map((u) => (u.id === id ? { ...u, ...updated } : u)));
    } catch (e) {
      setError(e.message);
      await reload();
    } finally {
      setBusyId(null);
    }
  };

  const onActivate = (u) => patch(u.id, { status: 'ACTIVE' });
  const onDisable = (u) => {
    if (!window.confirm(`Désactiver le compte ${u.email} ?`)) return;
    patch(u.id, { status: 'DISABLED' });
  };
  /**
   * Connecteur Claude (serveur MCP). Ouvrir transmet les données que la personne
   * consulte à Claude (Anthropic, hors Suisse) : confirmation explicite.
   */
  const onMcpToggle = (u) => {
    const grant = !u.mcpAccess;
    const msg = grant
      ? `Ouvrir le connecteur Claude à ${u.email} ?\n\nLa personne pourra interroger Synolog depuis Claude, avec ses droits habituels. Les données consultées sont transmises à Anthropic (hors Suisse) : accès à réserver aux personnes validées par la direction.`
      : `Fermer le connecteur Claude pour ${u.email} ? (effet immédiat)`;
    if (!window.confirm(msg)) return;
    patch(u.id, { mcpAccess: grant });
  };
  const onRole = (u, role) => {
    if (role === u.role) return;
    patch(u.id, { role });
  };

  const onImpersonateClick = async (u) => {
    if (!onImpersonate) return;
    if (
      !window.confirm(
        `Se connecter en tant que ${u.name || u.email} ?\n\nVous quitterez vos droits administrateur jusqu'à ce que vous reveniez à votre compte.`,
      )
    )
      return;
    setBusyId(u.id);
    setError(null);
    try {
      await onImpersonate(u.id);
      // Succès : le hook recharge l'utilisateur courant ; cette vue (admin only)
      // est alors démontée au profit de l'Accueil.
    } catch (e) {
      setError(e.message);
      setBusyId(null);
    }
  };

  const pendingCount = items.filter((u) => u.status === 'PENDING').length;

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1200, margin: '0 auto' }}>
      <div
        style={{
          position: 'sticky',
          top: 0,
          background: 'var(--bg)',
          paddingBottom: 12,
          zIndex: 5,
          marginBottom: 16,
        }}
      >
        <h1
          style={{
            margin: 0,
            fontSize: 22,
            color: 'var(--text)',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <Users size={20} /> Utilisateurs
          {pendingCount > 0 ? (
            <span
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: '#fff',
                background: 'var(--warning)',
                borderRadius: 999,
                padding: '2px 10px',
              }}
            >
              {pendingCount} en attente
            </span>
          ) : null}
        </h1>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            marginTop: 14,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'inline-flex', gap: 8 }}>
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                style={pill(statusFilter === f.key)}
                onClick={() => setStatusFilter(f.key)}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              marginLeft: 'auto',
              border: '1px solid var(--glass-border)',
              borderRadius: 'var(--radius-sm)',
              padding: '4px 10px',
              background: 'var(--glass)',
            }}
          >
            <Search size={14} style={{ color: 'var(--text-muted)' }} />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher (email, nom)…"
              style={{
                background: 'transparent',
                border: 'none',
                outline: 'none',
                color: 'var(--text)',
                fontSize: 13,
                width: 220,
              }}
            />
          </div>
        </div>
      </div>

      {error ? <div style={{ color: 'var(--danger)', marginBottom: 12 }}>{error}</div> : null}

      <div
        style={{
          background: 'var(--card-bg)',
          border: '1px solid var(--glass-border)',
          borderRadius: 'var(--radius-md)',
          overflow: 'hidden',
          boxShadow: 'var(--shadow-card)',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: 'var(--glass)', textAlign: 'left' }}>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>Utilisateur</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>Domaine</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>Rôle</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>Statut</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>Claude</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500, width: 120 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} style={{ padding: 20, color: 'var(--text-muted)' }}>Chargement…</td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ padding: 20, color: 'var(--text-muted)' }}>Aucun utilisateur.</td>
              </tr>
            ) : (
              items.map((u) => {
                const isSelf = u.id === user?.id;
                const busy = busyId === u.id;
                return (
                  <tr key={u.id} style={{ borderTop: '1px solid var(--glass-border)', opacity: busy ? 0.6 : 1 }}>
                    <td style={{ padding: '10px 12px', color: 'var(--text)' }}>
                      <div style={{ fontWeight: 500 }}>
                        {u.name || '—'}
                        {isSelf ? (
                          <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--text-muted)' }}>(vous)</span>
                        ) : null}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{u.email}</div>
                    </td>
                    <td style={{ padding: '10px 12px', color: 'var(--text-muted)', fontSize: 13 }}>{u.domain}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <select
                        value={u.role}
                        disabled={busy || (isSelf && u.role === 'ADMIN')}
                        onChange={(e) => onRole(u, e.target.value)}
                        style={selectStyle}
                        title={isSelf && u.role === 'ADMIN' ? 'Vous ne pouvez pas retirer votre propre rôle admin' : 'Changer le rôle'}
                      >
                        {ROLES.map((r) => (
                          <option key={r} value={r}>{r}</option>
                        ))}
                      </select>
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <StatusBadge status={u.status} />
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <McpToggle user={u} disabled={busyId === u.id} onClick={() => onMcpToggle(u)} />
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ display: 'inline-flex', gap: 6 }}>
                        {u.status === 'ACTIVE' ? (
                          <button
                            type="button"
                            onClick={() => onDisable(u)}
                            disabled={busy || isSelf}
                            title={isSelf ? 'Vous ne pouvez pas désactiver votre propre compte' : 'Désactiver'}
                            aria-label="Désactiver"
                            style={{ ...iconBtn, color: 'var(--danger)', opacity: isSelf ? 0.4 : 1 }}
                          >
                            <CirclePower size={15} />
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => onActivate(u)}
                            disabled={busy}
                            title={u.status === 'PENDING' ? 'Valider et activer' : 'Réactiver'}
                            aria-label="Activer"
                            style={{ ...iconBtn, color: 'var(--success)' }}
                          >
                            <CirclePower size={15} />
                          </button>
                        )}
                        {(() => {
                          // Impersonation : compte cible ACTIVE, non-admin, et pas soi-même.
                          const canImpersonate =
                            !isSelf && u.status === 'ACTIVE' && u.role !== 'ADMIN';
                          if (!canImpersonate) return null;
                          return (
                            <button
                              type="button"
                              onClick={() => onImpersonateClick(u)}
                              disabled={busy}
                              title={`Se connecter en tant que ${u.name || u.email}`}
                              aria-label="Se connecter en tant que cet utilisateur"
                              style={{ ...iconBtn, color: 'var(--accent)' }}
                            >
                              <VenetianMask size={15} />
                            </button>
                          );
                        })()}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Pastille on/off du connecteur Claude ; « via config » = ouvert par MCP_ALLOWED_EMAILS. */
function McpToggle({ user, disabled, onClick }) {
  const on = Boolean(user.mcpAccess || user.mcpAccessViaConfig);
  const title = user.mcpAccess
    ? 'Connecteur Claude ouvert — cliquer pour fermer'
    : user.mcpAccessViaConfig
      ? 'Ouvert par la configuration (MCP_ALLOWED_EMAILS) — cliquer pour reporter l’accès dans l’écran'
      : 'Connecteur Claude fermé — cliquer pour ouvrir';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-pressed={on}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '2px 10px',
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
        cursor: disabled ? 'wait' : 'pointer',
        background: on ? 'var(--accent)' : 'var(--glass)',
        color: on ? '#fff' : 'var(--text-muted)',
        border: `1px solid ${on ? 'var(--accent)' : 'var(--glass-border)'}`,
      }}
    >
      <Bot size={12} />
      {on ? (user.mcpAccess ? 'Oui' : 'Config') : 'Non'}
    </button>
  );
}
